import React, { useState, useEffect } from "react";
import { Card, Badge, Image, Button, Spinner, Modal } from "react-bootstrap";
import { toast } from "sonner";
import {
  ThumbsUp,
  MessageSquare,
  Bookmark,
  Send,
  Info,
} from "lucide-react";
import { FiX } from "react-icons/fi";
import { ReactComponent as ChevronRightIcon } from "../../../../assets/images/btn-right-aero.svg";
import { FacebookSelector } from "@charkour/react-reactions";
import { useNavigate } from "react-router-dom";
import communityService from "../../../../api/communityService";
import { mapPostsFromAPI } from "../../adapters";
import usePlan from "../../../../hooks/usePlan";
import { getUser } from "../../../../utils/auth";
import RestrictionModal from "../../../restriction-modals/RestrictionModal";
import UserProfileModal from "../user-profile-modal/UserProfileModal";
import {
  linkifyText,
  shortRole,
  shortSpecialty,
  categoryBg,
  sanitizePreviewContent,
  htmlToPlainText,
} from "../../utils/communityHelpers";
import parse from "html-react-parser";
import Linkify from "linkify-react";
import wowImg from "../../../../assets/images/Community_emojis/wow.png";
import hahaImg from "../../../../assets/images/Community_emojis/haha.png";
import loveImg from "../../../../assets/images/Community_emojis/Heart.png";
import angryImg from "../../../../assets/images/Community_emojis/angry.png";
import sadImg from "../../../../assets/images/Community_emojis/sad.png";
import "./RightSidebar.scss";

// Same emoji set PostCard uses — keeps the picker visually consistent.
const reactionIconFor = (reaction) => {
  switch (reaction) {
    case "like":
      return <ThumbsUp size={18} fill="currentColor" />;
    case "love":
      return <img src={loveImg} alt="love" width={18} height={18} />;
    case "haha":
      return <img src={hahaImg} alt="haha" width={18} height={18} />;
    case "wow":
      return <img src={wowImg} alt="wow" width={18} height={18} />;
    case "sad":
      return <img src={sadImg} alt="sad" width={18} height={18} />;
    case "angry":
      return <img src={angryImg} alt="angry" width={18} height={18} />;
    default:
      return <ThumbsUp size={18} />;
  }
};

/**
 * RightSidebar
 * Compact mirror of the main feed's PostCard, scaled for the narrow
 * community sidebar. Same fields and actions as PostCard — header with
 * role subtitle and Follow button, category badge + time row, title,
 * content with a "View detail" link to the full post, and the icon-only action row
 * (Like / Comment / Share on the left, Save / Info on the right).
 */
// Same reasons PostCard offers — keeps the sidebar's Info/Report flow
// behaviourally identical to the main feed.
const REPORT_REASONS = [
  "Spam",
  "Nudity",
  "Scam",
  "Hate Speech",
  "Harassment",
  "Violence",
  "Misinformation",
  "Terrorism",
  "Illegal Activity",
  "Child Exploitation",
];

const RightSidebar = ({
  onShowArticle,
  onSharePost,
  onLikePost,
  onReaction,
  onReport,
  onCategorySelect,
}) => {
  const [trendingPosts, setTrendingPosts] = useState([]);
  const [hotTopics, setHotTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Reaction-picker hover state, keyed by post id. Mirrors PostCard's
  // showReactionPicker pattern so the popover appears on hover and
  // dismisses with a small delay on leave.
  const [pickerOpenFor, setPickerOpenFor] = useState(null);
  const [pickerCloseTimer, setPickerCloseTimer] = useState(null);
  // Report modal state — which post is being reported + selected reason.
  const [reportTargetPost, setReportTargetPost] = useState(null);
  const [reportReason, setReportReason] = useState("");
  // Restriction + unfollow state — mirrors PostCard so sidebar follow gating
  // is identical (free users: 1 lifetime follow then paywall; unfollow asks
  // for confirmation).
  const [showPaywallSheet, setShowPaywallSheet] = useState(false);
  // Save/bookmark restriction shown when a free user tries to save a post
  const [showSaveRestriction, setShowSaveRestriction] = useState(false);
  const [unfollowTargetPost, setUnfollowTargetPost] = useState(null);
  const [followLoadingId, setFollowLoadingId] = useState(null);
  // Track in-flight save toggles so a double-click can't double-fire.
  const [saveLoadingId, setSaveLoadingId] = useState(null);
  // Which post's author profile modal is open (null = closed).
  const [profileTargetPost, setProfileTargetPost] = useState(null);

  const navigate = useNavigate();
  // True for any paid plan (matches PostCard). Drives the free-user follow gate.
  const { isPaid: isProUser } = usePlan();
  const currentUser = getUser();
  const currentUserId = currentUser?.id;

  useEffect(() => {
    fetchSidebarData();
  }, []);

  const fetchSidebarData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [trendingResponse, hotResponse] = await Promise.all([
        communityService.getTrendingPosts(),
        communityService.getHotTopics(),
      ]);
      const trendingData = trendingResponse?.data?.data?.posts || [];
      const hotData = hotResponse?.data?.data?.posts || [];
      setTrendingPosts(mapPostsFromAPI(trendingData));
      setHotTopics(mapPostsFromAPI(hotData));
    } catch (err) {
      console.error("Error fetching sidebar data:", err);
      setError("Failed to load community highlights");
    } finally {
      setLoading(false);
    }
  };

  const navigateToPostDetails = (post) => {
    navigate(`/community/${post.id}`, {
      state: { comment_id: parseInt(post?.id), userinfo: post },
    });
  };

  // Mirrors PostCard.handleUserProfileClick: opens the author profile modal,
  // gated by the same free-user view-count restriction.
  const handleAuthorClick = (e, post) => {
    if (e) e.stopPropagation();
    const meta = post._metadata || {};
    const isAnonymous = meta.isAnonymous === 1;
    const isOwn = !!meta.userId && meta.userId === currentUserId;
    const isDeleted = !meta.userId && !isAnonymous;
    if (isAnonymous || isOwn || isDeleted) return;

    // Free users cannot open the profile modal at all — show the upgrade modal
    if (!isProUser) {
      setShowPaywallSheet(true);
      return;
    }
    setProfileTargetPost(post);
  };

  // Helper to apply the same patch across both lists. Avoids repeating the
  // setTrendingPosts/setHotTopics pair for every optimistic mutation.
  const patchPost = (postId, patcher) => {
    const apply = (prev) =>
      prev.map((p) => (p.id === postId ? patcher(p) : p));
    setTrendingPosts(apply);
    setHotTopics(apply);
  };

  // Plain like-button click. Toggles 'like' on/off — same behavior as
  // PostCard's handleLike when no other reaction is active.
  const handleLikeClick = async (post) => {
    const current = post.userReaction;
    const next = current === "like" ? null : "like";
    patchPost(post.id, (p) => ({
      ...p,
      userReaction: next,
      liked: next != null,
      likes: Math.max(0, (p.likes || 0) + (next ? (current ? 0 : 1) : -1)),
    }));
    try {
      if (onReaction) await onReaction(post.id, "like");
      else if (onLikePost) await onLikePost(post.id);
    } catch (err) {
      console.error("Sidebar like failed:", err);
    }
  };

  // Pick a non-default reaction from the hover popover.
  const handleReactionPick = async (post, reaction) => {
    setPickerOpenFor(null);
    const current = post.userReaction;
    const next = current === reaction ? null : reaction;
    patchPost(post.id, (p) => ({
      ...p,
      userReaction: next,
      liked: next != null,
      likes: Math.max(0, (p.likes || 0) + (next ? (current ? 0 : 1) : -1)),
    }));
    try {
      if (onReaction) await onReaction(post.id, reaction);
    } catch (err) {
      console.error("Sidebar reaction failed:", err);
    }
  };

  // Report flow — mirrors PostCard: click Info → modal with reasons,
  // pick one → onReport(postId, reason). Falls back to a direct
  // communityService call if no parent handler was passed.
  const openReportModal = (post) => {
    setReportTargetPost(post);
    setReportReason("");
  };
  const closeReportModal = () => {
    setReportTargetPost(null);
    setReportReason("");
  };
  const submitReport = async () => {
    if (!reportTargetPost || !reportReason) {
      toast.error("Please select a reason for reporting");
      return;
    }
    try {
      if (onReport) {
        await onReport(reportTargetPost.id, reportReason);
      } else {
        await communityService.reportPost(reportTargetPost.id, reportReason);
        toast.success("Post reported successfully");
      }
    } catch (err) {
      console.error("Sidebar report failed:", err);
    } finally {
      closeReportModal();
    }
  };

  const openPicker = (postId) => {
    if (pickerCloseTimer) {
      clearTimeout(pickerCloseTimer);
      setPickerCloseTimer(null);
    }
    setPickerOpenFor(postId);
  };
  const schedulePickerClose = () => {
    const t = setTimeout(() => setPickerOpenFor(null), 250);
    setPickerCloseTimer(t);
  };

  // Broadcast that this author's follow state changed so other PostCard /
  // sidebar instances by the same author re-sync without a refetch.
  const broadcastFollowChange = (userId, isFollowing) => {
    window.dispatchEvent(
      new CustomEvent("community:follow-change", {
        detail: { userId, isFollowing },
      }),
    );
  };

  // Listen for follow changes from other components (e.g. the main feed
  // PostCard) and reflect them on matching sidebar cards.
  useEffect(() => {
    const handler = (event) => {
      const { userId, isFollowing } = event.detail || {};
      if (!userId) return;
      const flip = (p) =>
        p._metadata?.userId === userId ? { ...p, isFollowing } : p;
      setTrendingPosts((prev) => prev.map(flip));
      setHotTopics((prev) => prev.map(flip));
    };
    window.addEventListener("community:follow-change", handler);
    return () =>
      window.removeEventListener("community:follow-change", handler);
  }, []);

  const handleFollowToggle = async (post) => {
    const meta = post._metadata || {};
    const userId = meta.userId;
    if (!userId || followLoadingId) return;

    // Unfollow path: open the confirmation modal (same UX as PostCard).
    if (post.isFollowing) {
      setUnfollowTargetPost(post);
      return;
    }

    // Free-user gate: free users cannot follow anyone — show the upgrade modal.
    if (!isProUser) {
      setShowPaywallSheet(true);
      return;
    }

    setFollowLoadingId(post.id);
    // Optimistic flip across both lists.
    const flip = (p) =>
      p._metadata?.userId === userId ? { ...p, isFollowing: true } : p;
    setTrendingPosts((prev) => prev.map(flip));
    setHotTopics((prev) => prev.map(flip));
    try {
      const response = await communityService.follow(userId);
      if (response?.data?.success) {
        broadcastFollowChange(userId, true);
        toast.success(response?.data?.message || "Followed successfully");
      } else {
        // Revert if backend says no.
        const revert = (p) =>
          p._metadata?.userId === userId ? { ...p, isFollowing: false } : p;
        setTrendingPosts((prev) => prev.map(revert));
        setHotTopics((prev) => prev.map(revert));
      }
    } catch (err) {
      console.error("Sidebar follow failed:", err);
      toast.error(err?.response?.data?.message || "Failed to follow user");
      const revert = (p) =>
        p._metadata?.userId === userId ? { ...p, isFollowing: false } : p;
      setTrendingPosts((prev) => prev.map(revert));
      setHotTopics((prev) => prev.map(revert));
    } finally {
      setFollowLoadingId(null);
    }
  };

  const confirmUnfollow = async () => {
    const post = unfollowTargetPost;
    if (!post) return;
    const userId = post._metadata?.userId;
    if (!userId) return;
    setFollowLoadingId(post.id);
    try {
      const response = await communityService.unfollow(userId);
      if (response?.data?.success) {
        const flip = (p) =>
          p._metadata?.userId === userId ? { ...p, isFollowing: false } : p;
        setTrendingPosts((prev) => prev.map(flip));
        setHotTopics((prev) => prev.map(flip));
        broadcastFollowChange(userId, false);
        toast.success(
          response?.data?.message || `Unfollowed ${post.author || "user"}`,
        );
      }
    } catch (err) {
      console.error("Sidebar unfollow failed:", err);
      toast.error("Failed to unfollow user");
    } finally {
      setFollowLoadingId(null);
      setUnfollowTargetPost(null);
    }
  };
  const cancelUnfollow = () => setUnfollowTargetPost(null);

  const handleSaveToggle = async (post) => {
    // Match PostCard guards: skip if already in-flight or post is optimistic.
    if (!post?.id || saveLoadingId === post.id || post._optimistic) return;

    // Free users cannot save posts — show the upgrade modal. Un-saving an
    // already-saved post is still allowed for everyone.
    if (!isProUser && !post.isSaved) {
      setShowSaveRestriction(true);
      return;
    }

    const previous = post.isSaved;
    setSaveLoadingId(post.id);
    patchPost(post.id, (p) => ({ ...p, isSaved: !previous }));
    try {
      const response = await communityService.togglePostSave(post.id);
      if (response?.data?.success) {
        toast.success(
          response?.data?.message ||
          (!previous ? "Post saved" : "Removed from saved"),
        );
      } else {
        patchPost(post.id, (p) => ({ ...p, isSaved: previous }));
        toast.error(response?.data?.message || "Failed to update save");
      }
    } catch (err) {
      console.error("Sidebar save failed:", err);
      patchPost(post.id, (p) => ({ ...p, isSaved: previous }));
      toast.error(err?.response?.data?.message || "Failed to update save");
    } finally {
      setSaveLoadingId(null);
    }
  };

  const renderPostItem = (post) => {
    const meta = post._metadata || {};
    const isAnonymous = meta.isAnonymous === 1;
    const subtitleParts = !isAnonymous
      ? [
        shortRole(meta.userRole),
        shortSpecialty(meta.userPrimarySpecialty),
        meta.userCity || meta.userState,
      ].filter(Boolean)
      : [];
    // Mirrors PostCard's canFollow: skip own posts, anonymous, deleted-user,
    // and optimistic-placeholder posts.
    const isOwnPost = !!meta.userId && meta.userId === currentUserId;
    const canFollow =
      !!meta.userId &&
      !isOwnPost &&
      !isAnonymous &&
      !post._optimistic;
    const avatarFallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(
      isAnonymous ? "Anonymous" : post.author || "User",
    )}&background=0195D3&color=fff&size=40`;

    const content = post.content || "";
    const contentHasHtml =
      typeof content === "string" && /<\/?[a-z][\s\S]*>/i.test(content);
    const EXCERPT_LEN = 160;
    // Truncate long content and surface a "View detail" link that navigates
    // to the full post page. No inline expand — sidebar stays compact.
    // Truncation is measured/applied on the plain text, not the raw HTML —
    // slicing raw HTML by character index can cut a tag or entity in half
    // (e.g. a literal "&nb" left dangling from a chopped "&nbsp;").
    const plainText = contentHasHtml ? htmlToPlainText(content) : content;
    const isTruncated = plainText.length > EXCERPT_LEN;
    const displayed = isTruncated
      ? plainText.slice(0, EXCERPT_LEN).trimEnd() + "…"
      : content;
    // Once truncated we're working with plain text (no tags survived the
    // slice), so always render it through the plain-text/linkify path below
    // instead of re-parsing it as HTML.
    const renderAsHtml = contentHasHtml && !isTruncated;

    return (
      <div key={post.id} className="trending-post sidebar-post-card">
        {/* Header: avatar + name/role + Follow.
            Author click opens UserProfileModal (with paywall gate for free
            users) — same behaviour as PostCard. */}
        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
          <div
            className="d-flex gap-2 align-items-center flex-grow-1"
            style={{ cursor: "pointer", minWidth: 0 }}
            onClick={(e) => handleAuthorClick(e, post)}
          >
            <Image
              src={
                isAnonymous
                  ? avatarFallback
                  : `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${post.authorImage}`
              }
              roundedCircle
              width={36}
              height={36}
              className="flex-shrink-0"
              alt={isAnonymous ? "Anonymous" : post.author}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = avatarFallback;
              }}
            />
            <div style={{ minWidth: 0 }}>
              <div className="author-name text-truncate">
                {isAnonymous ? "Anonymous" : post.author}
              </div>
              {subtitleParts.length > 0 && (
                <small
                  className="text-muted d-block text-truncate"
                  style={{ fontSize: "0.8125rem", lineHeight: "18px", color: "var(--mmp-text-secondary)" }}
                >
                  {subtitleParts.join(" · ")}
                </small>
              )}
            </div>
          </div>
          {canFollow && (
            <Button
              variant={post.isFollowing ? "outline-secondary" : "outline-primary"}
              size="sm"
              className="btn-follow-sidebar flex-shrink-0"
              style={{ borderRadius: 16, fontSize: 12, padding: "2px 10px" }}
              onClick={(e) => {
                e.stopPropagation();
                handleFollowToggle(post);
              }}
              disabled={followLoadingId === post.id}
            >
              {followLoadingId === post.id
                ? "..."
                : post.isFollowing
                  ? "Following"
                  : "Follow"}
            </Button>
          )}
        </div>

        {/* Category badge + time */}
        {(post.categoryTag || post.time) && (
          <div className="d-flex justify-content-between align-items-center mb-2">
            {post.categoryTag ? (
              <Badge
                bg=""
                style={{
                  backgroundColor: categoryBg(post.categoryTag),
                  color: "#364152",
                  fontWeight: 500,
                }}
              >
                {post.categoryTag}
              </Badge>
            ) : (
              <span />
            )}
            {post.time && (
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 400,
                  color: "var(--mmp-text-secondary)",
                  lineHeight: "16px",
                }}
              >
                {post.time}
              </span>
            )}
          </div>
        )}

        {/* Title — typography driven by SCSS (.trending-post h6) so it
            matches PostCard. */}
        {post.title && (
          <h6
            style={{ cursor: "pointer" }}
            onClick={() => navigateToPostDetails(post)}
          >
            {post.title}
          </h6>
        )}

        {/* Content — typography driven by SCSS (.trending-post .post-text). */}
        {(() => {
          return renderAsHtml ? (
            <Linkify>
            <div
              className="post-text rich-content"
              style={{ cursor: "pointer" }}
              onClick={() => navigateToPostDetails(post)}
            >
              {parse(sanitizePreviewContent(displayed))}
            </div>
            </Linkify>
          ) : (
            <p
              className="post-text"
              style={{ cursor: "pointer" }}
              onClick={() => navigateToPostDetails(post)}
            >
              {linkifyText(displayed).map((part, i) =>
                part.type === "link" ? (
                  <a
                    key={i}
                    href={part.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      color: "var(--mmp-accent)",
                      textDecoration: "underline",
                      wordBreak: "break-all",
                    }}
                  >
                    {part.content}
                  </a>
                ) : (
                  <span key={i}>{part.content}</span>
                ),
              )}
            </p>
          );
        })()}
        {isTruncated && (
          <button
            type="button"
            className="sidebar-see-more"
            onClick={(e) => {
              e.stopPropagation();
              navigateToPostDetails(post);
            }}
            style={{
              background: "none",
              border: "none",
              color: "var(--mmp-accent)",
              padding: 0,
              fontSize: 13,
              fontWeight: 500,
              marginBottom: 4,
              cursor: "pointer",
            }}
          >
            View detail
          </button>
        )}

        {/* Icon-only actions row + Info on the right */}
        <div
          className="d-flex justify-content-between align-items-center sidebar-actions"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="d-flex align-items-center gap-1">
            <div
              className="position-relative"
              onMouseEnter={() => openPicker(post.id)}
              onMouseLeave={schedulePickerClose}
            >
              <Button
                variant="link"
                size="sm"
                className={`p-1 d-flex align-items-center gap-1 ${post.userReaction ? "text-primary" : "text-muted"
                  }`}
                onClick={() => handleLikeClick(post)}
                title="Like"
              >
                {reactionIconFor(post.userReaction)}
                {post.likes > 0 && <small>{post.likes}</small>}
              </Button>
              {pickerOpenFor === post.id && (
                <div
                  className="reaction-picker-popup"
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: 0,
                    zIndex: 1000,
                    marginBottom: 8,
                  }}
                  onMouseEnter={() => openPicker(post.id)}
                  onMouseLeave={schedulePickerClose}
                >
                  <FacebookSelector
                    onSelect={(reaction) => handleReactionPick(post, reaction)}
                  />
                </div>
              )}
            </div>
            <Button
              variant="link"
              size="sm"
              className="p-1 d-flex align-items-center gap-1 text-muted"
              onClick={() => navigateToPostDetails(post)}
              title="Comment"
            >
              <MessageSquare size={18} strokeWidth={2} />
              {post.comments > 0 && <small>{post.comments}</small>}
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-1 d-flex align-items-center text-muted"
              onClick={() => onSharePost?.(post)}
              title="Share"
            >
              <Send size={18} />
            </Button>
          </div>
          <div className="d-flex align-items-center gap-1">
            <Button
              variant="link"
              size="sm"
              className={`p-1 d-flex align-items-center ${post.isSaved ? "text-primary" : "text-muted"
                }`}
              onClick={() => handleSaveToggle(post)}
              disabled={saveLoadingId === post.id || post._optimistic}
              title="Save"
            >
              <Bookmark
                size={18}
                fill={post.isSaved ? "currentColor" : "none"}
              />
            </Button>
            {!isOwnPost && (
              <Button
                variant="link"
                size="sm"
                className="p-1 d-flex align-items-center text-muted"
                onClick={() => openReportModal(post)}
                title="Report"
                aria-label="Report"
              >
                <Info size={18} />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="sidebar-with-title">
        <h5>Community Highlights</h5>
        <div className="right-sidebar">
          <Card className="highlights-card">
            <Card.Body className="p-0">
              {loading && (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                  <p className="text-muted mt-2 mb-0">Loading highlights...</p>
                </div>
              )}
              {error && !loading && (
                <div className="alert alert-danger m-3" role="alert">
                  <small>{error}</small>
                </div>
              )}
              {!loading && !error && (
                <div className="section treding-posts">
                  <h6 className="section-title">Trending Posts</h6>
                  <div className="scrollable-section">
                    {trendingPosts.length > 0 ? (
                      trendingPosts.map(renderPostItem)
                    ) : (
                      <p className="text-muted text-center mb-0">
                        <small>No trending posts yet</small>
                      </p>
                    )}
                  </div>
                </div>
              )}
              {!loading && !error && (
                <div className="section hot-topics">
                  <h6 className="section-title">Hot Topics</h6>
                  <div className="scrollable-section">
                    {hotTopics.length > 0 ? (
                      hotTopics.map(renderPostItem)
                    ) : (
                      <p className="text-muted text-center pb-3 mb-0">
                        <small>No hot topics yet</small>
                      </p>
                    )}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Report Post Modal — markup matches PostCard exactly so it inherits
        the same global styles (custom-modal, radio-options, etc.). */}
      <Modal
        show={!!reportTargetPost}
        onHide={closeReportModal}
        centered
        size="lg"
        dialogClassName="custom-modal profile-other-details-modal"
      >
        <Modal.Header>
          <h5 className="modal-title">Report Post</h5>
          <Button className="close-btn" onClick={closeReportModal}>
            <FiX size={24} />
          </Button>
        </Modal.Header>

        <Modal.Body className="pt-0">
          <div className="radio-options">
            <div className="row">
              {REPORT_REASONS.map((reason) => (
                <div
                  key={reason}
                  className={`radio-option col-12 col-md-6 ${reportReason === reason ? "selected" : ""
                    }`}
                  onClick={() => setReportReason(reason)}
                >
                  <div className="contentwrap">
                    <input
                      type="radio"
                      name="reportReason"
                      id={`sidebar-${reason}`}
                      value={reason}
                      checked={reportReason === reason}
                      readOnly
                    />
                    <label htmlFor={`sidebar-${reason}`} className="radio-label">
                      {reason}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="buttons text-center mt-2">
            <Button
              variant="primary"
              onClick={submitReport}
              disabled={!reportReason}
            >
              Submit <ChevronRightIcon className="button-icon" />
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Author profile modal — same component PostCard uses; gating handled
        in handleAuthorClick. */}
      <UserProfileModal
        show={!!profileTargetPost}
        onHide={() => setProfileTargetPost(null)}
        userId={profileTargetPost?._metadata?.userId}
        userName={profileTargetPost?.author}
        userImage={profileTargetPost?.authorImage}
        userState={profileTargetPost?._metadata?.userState}
      />

      {/* Follow / profile restriction modal for non-pro users — same props
        as PostCard so the paywall copy stays identical. */}
      <RestrictionModal
        isVisible={showPaywallSheet}
        onClose={() => setShowPaywallSheet(false)}
        eventSource="follow_limit"
        iconType="user-follow"
        heading="Want to follow or chat with this user?"
        subText="Upgrade to connect and build real relationships."
        infoText="Pro users can follow, chat, and connect with anyone in the community."
      />

      {/* Save / bookmark restriction modal for non-pro users */}
      <RestrictionModal
        isVisible={showSaveRestriction}
        onClose={() => setShowSaveRestriction(false)}
        eventSource="save_limit"
        iconType="bookmark"
        heading="Want to save this post?"
        subText="Upgrade to Pro to bookmark posts and find them later."
        infoText="Pro users can save unlimited community posts to revisit anytime."
      />

      {/* Unfollow confirmation modal — same structure & classes as PostCard */}
      <Modal
        show={!!unfollowTargetPost}
        onHide={cancelUnfollow}
        centered
        size="sm"
        backdrop="static"
        dialogClassName="custom-modal profile-other-details-modal unfollow-confirmation-modal"
      >
        {!!unfollowTargetPost && (
          <div
            className="modal-backdrop-blur"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
              zIndex: 1040,
              pointerEvents: "none",
            }}
          />
        )}
        <div
          style={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            position: "relative",
            zIndex: 1050,
          }}
        >
          <Modal.Header>
            <h2 className="modal-title">Unfollow User</h2>
            <Button className="close-btn" onClick={cancelUnfollow}>
              <FiX size={24} />
            </Button>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              {unfollowTargetPost && (
                <>
                  <Image
                    src={
                      unfollowTargetPost.authorImage
                        ? `${process.env.REACT_APP_API_BASE_URL}/${
                            unfollowTargetPost.authorImage.startsWith(
                              "uploads/user_profile/",
                            )
                              ? unfollowTargetPost.authorImage
                              : `uploads/user_profile/${unfollowTargetPost.authorImage}`
                          }`
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            unfollowTargetPost.author || "User",
                          )}&background=0195D3&color=fff&size=60`
                    }
                    alt={unfollowTargetPost.author || "User"}
                    roundedCircle
                    width={60}
                    height={60}
                    className="mb-2"
                    style={{ objectFit: "cover" }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        unfollowTargetPost.author || "User",
                      )}&background=0195D3&color=fff&size=60`;
                    }}
                  />
                  <h6 className="mb-1">{unfollowTargetPost.author}</h6>
                  {unfollowTargetPost?._metadata?.userState && (
                    <p className="text-muted small mb-2">
                      {unfollowTargetPost._metadata.userState}
                    </p>
                  )}
                  <p className="small">
                    Are you sure you want to unfollow this user?
                  </p>
                </>
              )}
            </div>

            <div className="text-center mt-4">
              <Button
                size="sm"
                variant="outline-danger"
                onClick={confirmUnfollow}
                disabled={
                  !!unfollowTargetPost &&
                  followLoadingId === unfollowTargetPost.id
                }
                style={{ fontSize: "12px", padding: "4px 12px" }}
              >
                {unfollowTargetPost &&
                followLoadingId === unfollowTargetPost.id ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Unfollowing...
                  </>
                ) : (
                  "Unfollow"
                )}
              </Button>
            </div>
          </Modal.Body>
        </div>
      </Modal>
    </>
  );
};

export default RightSidebar;
